/* ============================================================
   /app — "Your card". Resolves the signed-in employee's own slug and
   hands off to the exact public CardPage render (same component,
   same Save-to-Contacts / PNG share / QR) — per the M14 spec, this
   route shares every component with the public card rather than
   duplicating it.
   ============================================================ */
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";

import { HttpError } from "../../lib/api";
import {
  authedGetJson,
  cacheSlug,
  getCachedSlug,
  isLoggedIn,
  refreshCachedSlug,
} from "../../lib/auth";

type MyCard = { slug: string; full_name: string | null; phone: string | null };

type State =
  | { kind: "loading" }
  | { kind: "ok"; slug: string }
  | { kind: "needs-onboarding" }
  | { kind: "no-card" }
  | { kind: "unauthenticated" };

export default function AppHome() {
  // A remembered slug short-circuits the whole thing: your card is public,
  // so showing it needs no session and no round trip. That's what makes the
  // home-screen icon open your card instantly instead of a spinner — or, if
  // the session has lapsed, instead of a login wall you didn't need to see.
  // Only ever set for a completed card, so the onboarding gate below still
  // catches first-run employees (they have nothing cached yet).
  const cachedSlug = getCachedSlug();
  const [state, setState] = useState<State>(() => {
    if (cachedSlug) return { kind: "ok", slug: cachedSlug };
    return isLoggedIn() ? { kind: "loading" } : { kind: "unauthenticated" };
  });

  useEffect(() => {
    if (state.kind === "ok" && cachedSlug) {
      // Redirecting away this tick; keep the cache honest for next launch.
      refreshCachedSlug();
      return;
    }
    if (state.kind !== "loading") return;
    let cancelled = false;
    authedGetJson<MyCard>("/api/me/card")
      .then((card) => {
        if (cancelled) return;
        if (!card.full_name || !card.phone) {
          setState({ kind: "needs-onboarding" });
        } else {
          cacheSlug(card.slug);
          setState({ kind: "ok", slug: card.slug });
        }
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (e instanceof HttpError && e.status === 401) {
          setState({ kind: "unauthenticated" });
        } else {
          setState({ kind: "no-card" });
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (state.kind === "unauthenticated") {
    return <Navigate to="/app/login" replace />;
  }
  if (state.kind === "needs-onboarding") {
    return <Navigate to="/app/profile" replace />;
  }
  if (state.kind === "ok") {
    return <Navigate to={`/${state.slug}`} replace />;
  }
  if (state.kind === "no-card") {
    return (
      <main className="min-h-screen flex items-center justify-center p-8 bg-paper text-center">
        <p className="text-ink/70 text-sm max-w-sm">
          Your account isn't linked to a card yet. Ask your admin to check your employee record.
        </p>
      </main>
    );
  }
  return (
    <main className="min-h-screen flex items-center justify-center p-8 bg-paper">
      <p className="text-ink/60 text-sm">Loading your card…</p>
    </main>
  );
}
